import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { OiqUiDropdownComponent } from '@oiq-ui-sdk/ui';
import { EMAIL_APP_STORE } from '@v2/modules/email-app/email-app.store';
import {
  EmailOAuthService,
  EmailProvider,
} from '@v2/modules/email-app/services/email-oauth.service';
import { Subject, takeUntil } from 'rxjs';
import { EmailLayoutService } from '../../services/email-layout.service';

@Component({
  selector: 'oiq-email-providers-switch',
  imports: [OiqUiDropdownComponent],
  templateUrl: './email-providers-switch.component.html',
  styleUrl: './email-providers-switch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailProvidersSwitchComponent implements OnDestroy {
  private emailOAuthService = inject(EmailOAuthService);
  private emailLayoutService = inject(EmailLayoutService);
  private store = inject(EMAIL_APP_STORE);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  @Output() providerDisconnected = new EventEmitter<void>();
  @Output() addMailboxClicked = new EventEmitter<void>();

  @ViewChild('providerDropdown') providerDropdown!: OiqUiDropdownComponent;

  mode = input<'dropdown' | 'mailbox'>('dropdown');

  currentProviderText = computed(() => {
    const account = this.store.currentAccount();
    if (account) return account;
    const provider = this.store.currentProvider();
    if (!provider) return '';
    return provider.startsWith('gmail') ? 'Gmail' : 'Outlook';
  });

  // All connected providers - directly from tokens, no filtering
  allConnectedProviders = computed(() => {
    const connectedData = this.store.connectedProvidersData();
    if (!connectedData?.tokens || !Array.isArray(connectedData.tokens)) return [];

    return connectedData.tokens.map((token: any) => ({
      value: token.provider as EmailProvider,
      label: token.provider.startsWith('gmail') ? 'Gmail' : 'Outlook',
      icon: token.provider.startsWith('gmail')
        ? '/assets/images/gmail.png'
        : '/assets/images/outlook.png',
      email: token.email || token.account,
    }));
  });

  onProviderSelect(provider: EmailProvider, account?: string): void {
    this.store.setCurrentProvider(provider, account);
    this.emailLayoutService.openList();
    this.providerDropdown?.close();
    // Navigate to inbox only when already on the email app page to reinitialize route state
    const currentPath = this.router.url.split('?')[0];
    if (currentPath.startsWith('/v2/email-app')) {
      this.router.navigate(['/v2/email-app'], { queryParams: { label: 'inbox' } });
    }
  }

  onDisconnectProvider(provider: EmailProvider, account?: string): void {
    this.emailOAuthService
      .logout(provider, account)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.store.clearCurrentProvider(provider);
          this.store.reloadConnectedProviders();
          this.providerDisconnected.emit();
        },
        error: err => {
          console.error('Error disconnecting provider:', err);
          this.store.clearCurrentProvider(provider);
          this.store.reloadConnectedProviders();
          this.providerDisconnected.emit();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
